import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, setToken } from '../lib/api';
import { authenticationOptionsForBrowser, registrationOptionsForBrowser, serializePasskeyCredential } from '../lib/passkeys';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = logged out, object = logged in
  const [user, setUser] = useState(null);
  const [totpChallenge, setTotpChallenge] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setToken(null);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.has('code') && query.has('state')) {
      setUser(false);
      return;
    }
    // Returning from Google OAuth: AuthCallback exchanges the session first
    if (window.location.hash?.includes('session_id=')) {
      setUser(false);
      return;
    }
    if (new URLSearchParams(window.location.search).has('magic')) return;
    checkAuth();
  }, [checkAuth]);

  const applyAuth = (data) => {
    if (data.mfa_required) {
      setToken(null);
      setUser(false);
      setTotpChallenge(data.challenge_id);
      return data;
    }
    setTotpChallenge(null);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => applyAuth((await api.post('/auth/login', { email, password })).data);
  const loginWithPasskey = async (identifier) => {
    const { data: ceremony } = await api.post('/auth/passkeys/login/options', { identifier });
    const credential = await navigator.credentials.get({ publicKey: authenticationOptionsForBrowser(ceremony.options) });
    if (!credential) throw new Error('Innlogginga blei avbroten.');
    const { data } = await api.post('/auth/passkeys/login/verify', {
      challenge_id: ceremony.challenge_id,
      credential: serializePasskeyCredential(credential),
    });
    return applyAuth(data);
  };
  const registerWithPasskey = async (email, name, username) => {
    const { data: ceremony } = await api.post('/auth/passkeys/register-account/options', { email, name, username });
    const credential = await navigator.credentials.create({ publicKey: registrationOptionsForBrowser(ceremony.options) });
    if (!credential) throw new Error('Registreringa blei avbroten.');
    const { data } = await api.post('/auth/passkeys/register-account/verify', {
      challenge_id: ceremony.challenge_id,
      credential: serializePasskeyCredential(credential),
    });
    return applyAuth(data);
  };
  const register = async (email, name, password, username) => applyAuth((await api.post('/auth/register', { email, name, password, username })).data);
  const requestMagicLink = async (email) => (await api.post('/auth/magic/request', { email })).data;
  const loginWithMagicToken = async (token) => applyAuth((await api.post('/auth/magic/verify', { token })).data);
  const loginWithAdminNumber = async (adminNumber, password) => applyAuth((await api.post('/auth/admin/login', { admin_number: adminNumber, password })).data);
  const loginWithOAuthCode = async (provider, code, state) => applyAuth((await api.post(`/auth/oauth/${provider}/callback`, { code, state })).data);
  const startOAuthLogin = async (provider) => { const { data } = await api.get(`/auth/oauth/${provider}/start`, { params: { origin: window.location.origin } }); window.location.href = data.url; };
  const loginWithGoogleSession = async (sessionId) => applyAuth((await api.post('/auth/google/session', { session_id: sessionId })).data);

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    setToken(null);
    setUser(false);
  };

  const startGoogleLogin = () => {
    return startOAuthLogin('google');
  };

  const cancelTotpChallenge = () => {
    setTotpChallenge(null);
    setToken(null);
    setUser(false);
  };

  const completeTotpChallenge = (data) => {
    setTotpChallenge(null);
    setToken(data.token);
    setUser(data.user);
    window.location.assign(data.user.is_admin ? '/admin' : '/medlem');
  };

  return (
    <AuthContext.Provider value={{ user, loading: user === null, setUser, login, loginWithPasskey, registerWithPasskey, logout, loginWithGoogleSession, startGoogleLogin, checkAuth, requestMagicLink, loginWithMagicToken, loginWithAdminNumber, loginWithOAuthCode, startOAuthLogin, totpChallenge, cancelTotpChallenge, completeTotpChallenge }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
